/* eslint max-statements: ["error", {max: 30}] -- this applies for the entire file */
/* eslint max-lines-per-function: ["error", {max: 210}] -- applies to entire file */
import { useCallback, useMemo, useState } from "react";

import isNil from "lodash/isNil";

import { BookingStepBaseTestIDMap } from "@/constants/Booking";
import { AnimationDuration } from "@/constants/UI";
import { router } from "@/crossPlatform/expo-router";
import useBookingNavigation from "@/hooks/useBookingNavigation";
import useAppMonitor from "@/services/AppMonitor";
import {
  useConfirmAppointmentMutation,
  useGetProviderTypeAttendeesQuery,
} from "@/store/api/schedule/booking";
import { useAppSelector } from "@/store/hooks";
import { useAppUserSelector } from "@/store/reducers/appUser";
import {
  selectAppointmentTitle,
  selectAppointmentTypeGroup,
  selectAppointmentTypeLabel,
  selectOptionalAttendees,
  selectPatientId,
  selectProviderType,
  selectRecurrence,
  selectTimeRange,
  useBookingDispatch,
} from "@/store/reducers/booking";
import AppointmentOverviewHeader from "@/ui/components/schedule/booking/AppointmentOverviewHeader";
import BookingModalFooter from "@/ui/components/schedule/booking/BookingModalFooter";
import type { BaseBookingStepProps } from "@/ui/components/schedule/booking/BookingStepMap";
import BookingStepTitle from "@/ui/components/schedule/booking/BookingStepTitle";
import ConfirmationAlert from "@/ui/composed/alerts/ConfirmationAlert";
import AvatarCard from "@/ui/core/AvatarCard";
import { FlatList } from "@/ui/layout";
import { getBookingAttendees } from "@/utils/booking.util";
import { joinTestIDs } from "@/utils/string.util";

const AppointmentConfirmationStep = ({ step }: BaseBookingStepProps) => {
  const baseTestID = BookingStepBaseTestIDMap[step];
  const { onBackwardNavigation } = useBookingNavigation({ currentStep: step });

  const recurrence = useAppSelector(selectRecurrence);
  const { externalId: appUserId, isPatient } = useAppUserSelector();
  const appointmentTitle = useAppSelector(selectAppointmentTitle);
  const bookingProvider = useAppSelector(selectProviderType);
  const patientExternalId = useAppSelector(selectPatientId);
  const typeGroup = useAppSelector(selectAppointmentTypeGroup);
  const optionalAttendees = useAppSelector(selectOptionalAttendees);
  const timeRange = useAppSelector(selectTimeRange);
  const appointmentType = useAppSelector(selectAppointmentTypeLabel);

  const { captureMessage } = useAppMonitor();

  const { showConfirmationAlert } = useBookingDispatch();

  const [isErrorAlertVisible, setIsErrorAlertVisible] =
    useState<boolean>(false);
  const [confirmAppointment, { isLoading, isSuccess }] =
    useConfirmAppointmentMutation();

  const { data: attendees } = useGetProviderTypeAttendeesQuery({
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- grandfathered in, should probably use a { skip: isEmpty(...) || isEmpty(...)
    patientExternalId: patientExternalId!,
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- grandfathered in, should probably use a { skip: isEmpty(...) || isEmpty(...)
    typeGroup: typeGroup!,
  });

  const handleBookingConfirmationSuccess = () => {
    router.dismiss();
    // only display the confirmation alert after some delay, to avoid a very sudden/abrupt/immediate alert
    // note: this does NOT have anything to do with the timing of the alert's animation; the default duration is simply reused here
    setTimeout(showConfirmationAlert, AnimationDuration.ModalDismiss);
  };

  const allAttendees = useMemo(() => {
    return getBookingAttendees({
      attendees,
      optionalAttendees,
      providerType: bookingProvider,
    });
  }, [bookingProvider, attendees, optionalAttendees]);

  const isPatientAttending = allAttendees.some((x) => x.userType === "patient");

  const providerAttendees = allAttendees.filter(
    (x) => x.userType === "provider",
  );

  const supportAttendees = allAttendees.filter((x) => x.userType === "support");

  const ListHeaderComponent = useCallback(() => {
    if (isNil(timeRange) || isNil(recurrence)) return null;
    return (
      <>
        <BookingStepTitle step={step} />
        <AppointmentOverviewHeader
          appointmentTitle={appointmentTitle}
          attendeeCount={allAttendees.length}
          paddingBottom={12}
          recurrence={recurrence}
          testID={baseTestID}
          timeRange={timeRange}
        />
      </>
    );
  }, [
    step,
    baseTestID,
    appointmentTitle,
    recurrence,
    timeRange,
    allAttendees.length,
  ]);

  if (isNil(timeRange) || isNil(recurrence)) {
    captureMessage(
      "Time range or recurrence is missing in AppointmentConfirmationStep",
    );
    return null;
  }

  const handleDismissErrorAlert = () => {
    setIsErrorAlertVisible(false);
  };

  const handleConfirmAppointment = () => {
    const providerHostExternalId = bookingProvider?.providerExternalId;

    confirmAppointment({
      appointmentEndDateTime: new Date(timeRange.endTime).toISOString(),
      appointmentStartDateTime: new Date(timeRange.startTime).toISOString(),
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- grandfathered in, should probably be checking before this
      appointmentType: appointmentType!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- grandfathered in, should probably be checking before this
      hostExternalId: providerHostExternalId!,
      isPatient,
      isPatientAttending,
      isRescheduleRequest: false,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- grandfathered in, should probably be checking before this
      patientExternalId: patientExternalId!,
      providerAttendeeExternalIds: providerAttendees
        .filter((x) => x.userId !== providerHostExternalId)
        .map((x) => x.userId),

      recurrenceFrequency: recurrence,
      supportAttendeeExternalIds: supportAttendees.map((x) => x.userId),
    })
      .unwrap()
      .then(() => {
        handleBookingConfirmationSuccess();
      })
      .catch(() => {
        setIsErrorAlertVisible(true);
      });
  };

  return (
    <>
      <FlatList
        data={allAttendees}
        ListHeaderComponent={ListHeaderComponent}
        renderItem={({ item }) => {
          const isAppUser = item.userId === appUserId;

          return (
            <AvatarCard
              isAppUser={isAppUser}
              userId={item.userId}
              userName={item.userName}
              userType={item.userType}
            />
          );
        }}
        testID={baseTestID}
      />
      <ConfirmationAlert
        confirmationType="destructive"
        confirmText="Dismiss"
        isOpen={isErrorAlertVisible}
        message={
          "We're sorry, we are unable to book your appointment at this time.\n\nPlease message your provider directly to request an appointment."
        }
        onConfirm={handleDismissErrorAlert}
        onDismiss={handleDismissErrorAlert}
        testID={joinTestIDs(baseTestID, "error")}
        title="Unable to book"
      />
      {!isSuccess && (
        <BookingModalFooter
          isFinalStep
          isLoading={isLoading}
          onBackwardPress={onBackwardNavigation}
          onForwardPress={handleConfirmAppointment}
          step={step}
        />
      )}
    </>
  );
};

export default AppointmentConfirmationStep;
